import type {
  InitGameOptions,
  RunHorsesBoard,
  RunHorsesCell,
  RunHorsesPlayer,
  RunHorsesPlayerId,
  RunHorsesPosition,
  RunHorsesState,
} from "./types";
import { RUN_HORSES_BOARD_SIZE, RUN_HORSES_PLAYERS } from "./types";
import { setCell } from "./utils";

// Two ㄴ-shaped clusters of 5 at opposite corners per player (rule.md §2).
const INITIAL_PIECES: Readonly<
  Record<RunHorsesPlayerId, readonly RunHorsesPosition[]>
> = {
  p1: [
    { row: 0, col: 0 },
    { row: 0, col: 1 },
    { row: 0, col: 2 },
    { row: 1, col: 0 },
    { row: 2, col: 0 },
    { row: 10, col: 10 },
    { row: 10, col: 9 },
    { row: 10, col: 8 },
    { row: 9, col: 10 },
    { row: 8, col: 10 },
  ],
  p2: [
    { row: 0, col: 10 },
    { row: 0, col: 9 },
    { row: 0, col: 8 },
    { row: 1, col: 10 },
    { row: 2, col: 10 },
    { row: 10, col: 0 },
    { row: 10, col: 1 },
    { row: 10, col: 2 },
    { row: 9, col: 0 },
    { row: 8, col: 0 },
  ],
};

export function createEmptyBoard(): RunHorsesBoard {
  return Array.from({ length: RUN_HORSES_BOARD_SIZE }, () =>
    Array.from(
      { length: RUN_HORSES_BOARD_SIZE },
      () => "empty" as RunHorsesCell,
    ),
  );
}

export function createInitialBoard(): RunHorsesBoard {
  let board = createEmptyBoard();
  for (const playerId of RUN_HORSES_PLAYERS) {
    for (const piece of INITIAL_PIECES[playerId]) {
      board = setCell(board, piece.row, piece.col, playerId);
    }
  }
  return board;
}

function createPlayer(playerId: RunHorsesPlayerId): RunHorsesPlayer {
  return {
    playerId,
    label: playerId === "p1" ? "Black" : "White",
    color: playerId === "p1" ? "black" : "white",
  };
}

function assertPlayerId(playerId: RunHorsesPlayerId, context: string): void {
  if (!RUN_HORSES_PLAYERS.includes(playerId)) {
    throw new Error(`${context} must be p1 or p2`);
  }
}

export function initGame(options: InitGameOptions = {}): RunHorsesState {
  const hostPlayerId = options.hostPlayerId ?? "p1";
  assertPlayerId(hostPlayerId, "Host player");
  if (options.firstPlayerId) {
    assertPlayerId(options.firstPlayerId, "First player");
  }
  // p1 (black) moves first by default.
  const firstPlayerId = options.firstPlayerId ?? "p1";

  return {
    gameId: options.gameId ?? `run-horses-${RUN_HORSES_PLAYERS.join("-")}`,
    hostPlayerId,
    players: RUN_HORSES_PLAYERS.map(createPlayer),
    phase: "playing",
    board: createInitialBoard(),
    currentPlayerId: firstPlayerId,
    moveNumber: 0,
    winnerId: null,
    lastAction: null,
  };
}
